import {FC, useCallback, useMemo} from 'react';
import {Form, SemanticWIDTHS} from 'semantic-ui-react';
import {Field, ListInput, ValueInput, ValueRenderer, ValueInputRendererProps} from 'apstra-ui-common';
import {get, compact, castArray, omit, map, mapValues} from 'lodash';

import './QueueSpecificThresholdInput.less';

const columns = {
  queue_id: {title: 'Queue ID', width: 3},
  discard_threshold: {title: 'Discard Threshold', width: 3},
  buffer_utilization_threshold: {title: 'Buffer Utilization Threshold', width: 3},
  ecn_threshold: {title: 'ECN Threshold', width: 3},
  pfc_threshold: {title: 'PFC Threshold', width: 3},
};
const generateNewEntry = () => mapValues(columns, () => '');
const isItemRemovable = (item) => item.queue_id !== 'default';

const QueueSpecificThresholdInput: FC<ValueInputRendererProps> = ({
  name, value = [], schema, required, disabled, errors, onChange, fieldProps
}) => {
  const onFieldChange = useCallback((index, field, newRowInputValue) => {
    const newInputValue = [...value];
    newInputValue[index][field] = newRowInputValue;
    onChange(newInputValue);
  }, [value, onChange]);

  const errorsByItems = useMemo(() => ListInput.getErrorByItems(errors), [errors]);
  const propertiesSchema = get(schema, ['items', 'properties'], {});

  return (
    <ListInput
      className='queue-specific-threshold-input'
      name={name}
      value={value}
      schema={schema}
      required={required}
      disabled={disabled}
      errors={errorsByItems.generic}
      onChange={onChange}
      generateNewEntry={generateNewEntry}
      isItemRemovable={isItemRemovable}
      minItems={1}
      header={<QueueSpecificThresholdHeader />}
      fieldProps={fieldProps}
    >
      {({value, disabled, index}) => map(columns, ({width}, key) => (
        <ValueInput
          key={key}
          value={value[key]}
          schema={
            key === 'queue_id' ?
              value.queue_id === 'default' ? {type: 'string'} : {type: 'integer', minimum: 0}
            :
              omit(propertiesSchema[key], ['title', 'description'])
          }
          disabled={disabled || (key === 'queue_id' && value.queue_id === 'default')}
          fieldProps={{width: width as SemanticWIDTHS}}
          errors={compact(castArray(errorsByItems.byIndex[index]?.[key]))}
          onChange={(rowInputValue) => onFieldChange(index, key, rowInputValue)}
        />
      ))}
    </ListInput>
  );
};

export default QueueSpecificThresholdInput;

const QueueSpecificThresholdHeader = () => (
  <Field className='field-subheader'>
    <Form.Group>
      {map(columns, ({title, width}, key) =>
        <Field key={key} width={width as SemanticWIDTHS} label={title} />
      )}
    </Form.Group>
  </Field>
);

export const queueSpecificThresholdRenderer = new ValueRenderer({
  condition: ({name, schema}) => name === 'queue_thresholds' && schema?.type === 'array',
  Value: () => null,
  ValueInput: QueueSpecificThresholdInput,
});
